import React, { useState, useEffect } from "react";
import {
  IconButton,
  Button,
  TextField,
  Modal,
  Box,
  Divider,
  Typography,
  Stack,
  Radio,
  FormControlLabel,
  RadioGroup,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { RegisterUser } from "../../redux/Actions/userStatusService";
import { AddUserSchema } from "../../Component/Schemas/userSchema";
// import { useResizeDetector } from 'react-resize-detector';

const AddManager = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  
  const [accessControl, setAccessControl] = useState({
    transactionManagement: { deposits: "", withdrawals: "" },
    userManagement: { adminUserCreation: "", adminAccessControl: "", managerUserCreation: "" },
    investorUserManagement: { editAccount: "", kyc: "", kycStatus: "" },
    kycManagement: { kycQuestions: "" },
  });

  // const { ref } = useResizeDetector({
  //   handleHeight: true,
  //   handleWidth: true,
  //   refreshMode: 'throttle',
  //   refreshRate: 1000,
  // });

  const handleAccessControlChange = (module, subModule) => (event) => {
    setAccessControl((prev) => ({
      ...prev,
      [module]: { ...prev[module], [subModule]: event.target.value },
    }));
  };

  const resetFormAndClose = () => {
    formik.resetForm();
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      role: "ADMIN",
    },
    validationSchema: AddUserSchema,
    onSubmit: (values) => {
      const bodyData = {
        email: values.email,
        password: values.password,
        role: values.role,
        userDetailsDto: { firstName: values.firstName, lastName: values.lastName },
        accessControl: values.role === "ADMIN" ? accessControl : {},
      };
      dispatch(RegisterUser(bodyData)).then(() => {
        resetFormAndClose();
      });
    },
  });

  // Workaround: force reflow to handle ResizeObserver loop error
  const handleRoleChange = (event) => {
    formik.handleChange(event);

    // Force layout recalculation to fix ResizeObserver issue
    setTimeout(() => {
      const forceReflow = document.body.offsetHeight; // Force reflow
    }, 0);
  };

  return (
    <Modal
      open={open}
      onClose={resetFormAndClose}
      aria-labelledby="form-dialog-title"
    >
      <Box
        // ref={ref}
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          minWidth: 400,
          maxWidth: 600,
          maxHeight: "90vh",
          overflowY: "auto",  // Add scroll when content overflows
        }}
      >
        <IconButton
          edge="end"
          color="inherit"
          onClick={resetFormAndClose}
          aria-label="close"
          sx={{ position: "absolute", top: 20, right: 20 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h5" component="h2">
          <b>Add New User</b>
        </Typography>
        <Divider sx={{ mt: 2, mb: 2 }} />

        <form onSubmit={formik.handleSubmit}>
          <TextField
            margin="dense"
            label="First Name"
            name="firstName"
            fullWidth
            required
            value={formik.values.firstName}
            onChange={formik.handleChange}
            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
            helperText={formik.touched.firstName && formik.errors.firstName}
          />

          <TextField
            margin="dense"
            label="Last Name"
            name="lastName"
            fullWidth
            required
            value={formik.values.lastName}
            onChange={formik.handleChange}
            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
            helperText={formik.touched.lastName && formik.errors.lastName}
          />

          <TextField
            margin="dense"
            label="Email"
            name="email"
            fullWidth
            required
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />

          <TextField
            margin="dense"
            label="Password"
            name="password"
            type="password"
            fullWidth
            required
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />

          <Typography variant="body1" sx={{ mt: 2 }}>
            Select Role:
          </Typography>
          <FormControlLabel
            control={
              <Radio
                checked={formik.values.role === "ADMIN"}
                onChange={handleRoleChange}
                value="ADMIN"
                name="role"
                color="primary"
              />
            }
            label="ADMIN"
          />
          <FormControlLabel
            control={
              <Radio
                checked={formik.values.role === "MANAGER"}
                onChange={handleRoleChange}
                value="MANAGER"
                name="role"
                color="primary"
              />
            }
            label="MANAGER"
          />

          {formik.values.role === "ADMIN" && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6">Access Control</Typography>
              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1">Transaction Management</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography>Deposits Management</Typography>
                </Grid>
                <Grid item xs={6}>
                  <RadioGroup
                    row
                    value={accessControl.transactionManagement.deposits}
                    onChange={handleAccessControlChange("transactionManagement", "deposits")}
                  >
                    <FormControlLabel value="view" control={<Radio />} label="View Access" />
                    <FormControlLabel value="edit" control={<Radio />} label="Edit Access" />
                  </RadioGroup>
                </Grid>

                <Grid item xs={6}>
                  <Typography>Withdrawals Management</Typography>
                </Grid>
                <Grid item xs={6}>
                  <RadioGroup
                    row
                    value={accessControl.transactionManagement.withdrawals}
                    onChange={handleAccessControlChange("transactionManagement", "withdrawals")}
                  >
                    <FormControlLabel value="view" control={<Radio />} label="View Access" />
                    <FormControlLabel value="edit" control={<Radio />} label="Edit Access" />
                  </RadioGroup>
                </Grid>
              </Grid>

              <Typography variant="subtitle1">Users Management</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography>Admin User Creation</Typography>
                </Grid>
                <Grid item xs={6}>
                  <RadioGroup
                    row
                    value={accessControl.userManagement.adminUserCreation}
                    onChange={handleAccessControlChange("userManagement", "adminUserCreation")}
                  >
                    <FormControlLabel value="view" control={<Radio />} label="View Access" />
                    <FormControlLabel value="edit" control={<Radio />} label="Edit Access" />
                  </RadioGroup>
                </Grid>

                <Grid item xs={6}>
                  <Typography>Admin Access Control</Typography>
                </Grid>
                <Grid item xs={6}>
                  <RadioGroup
                    row
                    value={accessControl.userManagement.adminAccessControl}
                    onChange={handleAccessControlChange("userManagement", "adminAccessControl")}
                  >
                    <FormControlLabel value="view" control={<Radio />} label="View Access" />
                    <FormControlLabel value="edit" control={<Radio />} label="Edit Access" />
                  </RadioGroup>
                </Grid>

                <Grid item xs={6}>
                  <Typography>Manager User Creation</Typography>
                </Grid>
                <Grid item xs={6}>
                  <RadioGroup
                    row
                    value={accessControl.userManagement.managerUserCreation}
                    onChange={handleAccessControlChange("userManagement", "managerUserCreation")}
                  >
                    <FormControlLabel value="view" control={<Radio />} label="View Access" />
                    <FormControlLabel value="edit" control={<Radio />} label="Edit Access" />
                  </RadioGroup>
                </Grid>
              </Grid>

              <Typography variant="subtitle1">Investor User Management</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography>Edit Account</Typography>
                </Grid>
                <Grid item xs={6}>
                  <RadioGroup
                    row
                    value={accessControl.investorUserManagement.editAccount}
                    onChange={handleAccessControlChange("investorUserManagement", "editAccount")}
                  >
                    <FormControlLabel value="view" control={<Radio />} label="View Access" />
                    <FormControlLabel value="edit" control={<Radio />} label="Edit Access" />
                  </RadioGroup>
                </Grid>

                <Grid item xs={6}>
                  <Typography>KYC</Typography>
                </Grid>
                <Grid item xs={6}>
                  <RadioGroup
                    row
                    value={accessControl.investorUserManagement.kyc}
                    onChange={handleAccessControlChange("investorUserManagement", "kyc")}
                  >
                    <FormControlLabel value="view" control={<Radio />} label="View Access" />
                    <FormControlLabel value="edit" control={<Radio />} label="Edit Access" />
                  </RadioGroup>
                </Grid>

                <Grid item xs={6}>
                  <Typography>KYC Status</Typography>
                </Grid>
                <Grid item xs={6}>
                  <RadioGroup
                    row
                    value={accessControl.investorUserManagement.kycStatus}
                    onChange={handleAccessControlChange("investorUserManagement", "kycStatus")}
                  >
                    <FormControlLabel value="view" control={<Radio />} label="View Access" />
                    <FormControlLabel value="edit" control={<Radio />} label="Edit Access" />
                  </RadioGroup>
                </Grid>
              </Grid>

              <Typography variant="subtitle1">KYC Management</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography>KYC Questions</Typography>
                </Grid>
                <Grid item xs={6}>
                  <RadioGroup
                    row
                    value={accessControl.kycManagement.kycQuestions}
                    onChange={handleAccessControlChange("kycManagement", "kycQuestions")}
                  >
                    <FormControlLabel value="view" control={<Radio />} label="View Access" />
                    <FormControlLabel value="edit" control={<Radio />} label="Edit Access" />
                  </RadioGroup>
                </Grid>
              </Grid>
            </Box>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
            <Button onClick={resetFormAndClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              Submit
            </Button>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
};

export default AddManager;
